import type { RootState } from 'src/redux/store';

import { Navigate, useLocation } from 'react-router';

import Box from '@mui/material/Box';

import { useAppSelector } from 'src/hook/hooks';
import { getPostLoginRedirectPath } from 'src/permissions/post-login-redirect';

import { BuyPlanPrompt } from 'src/components/management-user';

import { usePremissionContext } from './use-permissions-context';
import { PermissionLoadingScreen } from './permission-loading-screen';
import { getUserRole, hasBillingViewPermission } from './permission-utils';

export const PermissionWrapper = ({
  module,
  action,
  children,
  isAdminOnly,
}: {
  module: string | string[];
  action?: string;
  children: React.ReactNode;
  isAdminOnly?: boolean;
}) => {
  const { hasPermission, hasModuleActionPermission, permissions } = usePremissionContext();
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);

  const location = useLocation();

  if ((loginApiData as any)?.tenant?.id && !permissions) {
    return <PermissionLoadingScreen />;
  }

  const role = getUserRole(loginApiData);

  if (isAdminOnly && role !== 'Admin') {
    const redirectTarget = getPostLoginRedirectPath(loginApiData);
    if (redirectTarget && redirectTarget !== location.pathname) {
      return <Navigate to={redirectTarget} replace />;
    }
    return <Navigate to="/403" replace />;
  }

  // 🔹 Special case: allow `/setting/billing` if bill/billing view exists
  const isOnlyBilling =
    hasBillingViewPermission(permissions) &&
    Object.keys(permissions ?? {}).every(
      (key) =>
        key === 'billing' ||
        key === 'bill' ||
        !Object.values(permissions?.[key] || {}).some((v) => v === true)
    );

  if (location?.pathname === '/dashboard' && isOnlyBilling && (loginApiData as any)?.tenant?.id) {
    return <Navigate to="/setting/billing" replace />;
  }

  // 🔐 Module permission check
  const hasModulePermission = action
    ? hasModuleActionPermission(module, action)
    : hasPermission(module);

  if (!hasModulePermission) {
    if (role?.toLowerCase() === 'management') {
      return (
        <Box sx={{ py: 6, px: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <BuyPlanPrompt />
        </Box>
      );
    }

    const redirectTarget = getPostLoginRedirectPath(loginApiData);
    if (redirectTarget && redirectTarget !== location.pathname) {
      return <Navigate to={redirectTarget} replace />;
    }
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
};
